'use client';

import { c as _c } from "react/compiler-runtime";
import './sheet.scss';
import { PropsWithChildren, forwardRef } from 'react';
import { VisuallyHidden } from 'react-aria';
import { cn } from '../../utils/helpers.ts';
import { Modal, ModalClose, ModalCloseButton, ModalContent, ModalContentProps, ModalProps, ModalTitle, ModalTrigger, ModalTriggerProps } from '../modal/modal.tsx';
export type SheetProps = ModalProps;
export type SheetContentProps = ModalContentProps;
export const Sheet = Modal;
export const SheetHeader: React.FC<React.PropsWithChildren> = t0 => {
  const $ = _c(5);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <ModalTitle className="heading-xs lg:heading-sm">{children}</ModalTitle>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ModalCloseButton name="Sheet" className="Sheet__close mr-snug-lg lg:mr-comfy-xs" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1) {
    t3 = <header className="flex flex-shrink-0 justify-between items-center w-full mx-auto p-comfy-sm md:px-comfy-xl lg:py-comfy-md lg:max-w-md lg:px-0">{t1}{t2}</header>;
    $[3] = t1;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};
export const SheetBody: React.FC<React.PropsWithChildren> = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="Sheet__body flex-1 overflow-auto w-full pb-roomy-md"><div className="max-w-full lg:max-w-md mx-auto py-comfy-lg px-comfy-sm md:p-comfy-xl lg:px-0">{children}</div></div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
export interface SheetBodyHeaderProps extends PropsWithChildren<React.HTMLAttributes<HTMLDivElement>> {
  isSticky?: boolean;
}

/**
 * A sticky header component for SheetBody
 */
export const SheetBodyHeader: React.FC<SheetBodyHeaderProps> = t0 => {
  const $ = _c(12);
  let children;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      isSticky: t1,
      children,
      className: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
  } else {
    children = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
  }
  const isSticky = t1 === undefined ? false : t1;
  const className = t2 === undefined ? "" : t2;
  const t3 = `Sheet__body-header pt-comfy-xl -mt-comfy-xl top-0 ${className}`;
  let t4;
  if ($[5] !== isSticky || $[6] !== t3) {
    t4 = cn(t3, {
      "Sheet__body-header--sticky sticky": isSticky
    });
    $[5] = isSticky;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== children || $[9] !== props || $[10] !== t4) {
    t5 = <div className={t4} {...props}>{children}</div>;
    $[8] = children;
    $[9] = props;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
};
export const SheetFooter: React.FC<React.PropsWithChildren> = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="Sheet__footer absolute w-full bottom-0 left-0 flex-shrink-0 px-comfy-sm py-comfy-xs md:px-comfy-xl"><div className="mx-auto w-full lg:max-w-md">{children}</div></div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};

// TODO: Add functionality for "isNarrow" prop (where max-width of content changes): essentially default to the wide version (768px) and have a isNarrow or something property which would drive the more narrow constraint (500px). Note that this does not affect the Sheet width, just the max width of the content inside the sheet.
export const SheetContent = forwardRef<HTMLDivElement, SheetContentProps>((t0, ref) => {
  const $ = _c(15);
  let children;
  let props;
  let style;
  let title;
  if ($[0] !== t0) {
    ({
      style,
      children,
      title,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
    $[3] = style;
    $[4] = title;
  } else {
    children = $[1];
    props = $[2];
    style = $[3];
    title = $[4];
  }
  let t1;
  if ($[5] !== style) {
    t1 = {
      ...style
    };
    $[5] = style;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== title) {
    t2 = title && <VisuallyHidden>{title}</VisuallyHidden>;
    $[7] = title;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== children || $[10] !== props || $[11] !== ref || $[12] !== t1 || $[13] !== t2) {
    t3 = <ModalContent ref={ref} aria-describedby={undefined} className="Sheet__content w-full flex flex-col" style={t1} {...props}>{t2}{children}</ModalContent>;
    $[9] = children;
    $[10] = props;
    $[11] = ref;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  return t3;
});
/**
 * Sheet trigger.
 */
export type SheetTriggerProps = ModalTriggerProps;
export const SheetTrigger = ModalTrigger;

/**
 * Sheet close.
 */
export const SheetClose = ModalClose;
export const SheetActions: React.FC<React.PropsWithChildren> = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="flex items-center justify-center gap-comfy-xs md:gap-comfy-sm">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};